import React, { useCallback, useEffect, useMemo, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Settings2, Target } from "lucide-react";
import { subscribe } from "@/lib/liveSync";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import GoalRecordDialog from "./GoalRecordDialog";
import GoalRecordDetail from "./GoalRecordDetail";
import GoalCategoriesDialog from "./GoalCategoriesDialog";

const FILTERS = [
  { key: "unresolved", label: "Unresolved" },
  { key: "resolved", label: "Resolved" },
];

const ALL_TYPES = "__all__";

// G.O.A.L — Guest Opportunity Action Learning.
// Brand Catalog mounts this as the cross-customer view. A guest profile mounts
// the same component with `customerId` so both read/write the same records.
export default function GoalTab({ active, customerId = null, customerName = "", customer = null }) {
  const { staff } = useAuth();
  const canEdit = hasPermission(staff, "brand.customers.edit");
  const scoped = !!customerId;

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("unresolved");
  const [sourceType, setSourceType] = useState(ALL_TYPES);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ unresolved: 0, resolved: 0 });
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.get("/goal/records", {
        params: {
          search: scoped ? undefined : (q.trim() || undefined),
          status,
          source_type: sourceType === ALL_TYPES ? undefined : sourceType,
          customer_id: customerId || undefined,
        },
      });
      setRows(r.data?.records || []);
      setCounts(r.data?.counts || { unresolved: 0, resolved: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [q, status, sourceType, customerId, scoped]);

  const loadLookups = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        staffApi.get("/goal/categories"),
        staffApi.get("/goal/managers"),
      ]);
      setCategories(c.data?.categories || []);
      setSources(c.data?.sources || []);
      setSourceTypes(c.data?.source_types || []);
      setManagers(m.data?.managers || []);
    } catch { /* silent — the list still renders */ }
  }, []);

  useEffect(() => { if (active) loadLookups(); }, [active, loadLookups]);
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(loadRecords, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [active, loadRecords, q]);

  // Live refresh — any workstation creating or resolving a record repaints
  // every open G.O.A.L view (brand tab AND guest profile tab) with no reload.
  useEffect(() => {
    if (!active) return undefined;
    return subscribe("goal_updated", () => { loadRecords(); loadLookups(); });
  }, [active, loadRecords, loadLookups]);

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows]);
  const colSpan = scoped ? 7 : 8;

  return (
    <div className="space-y-4" data-testid="goal-tab">
      {scoped ? (
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="goal-title">
            <Target className="w-5 h-5 text-primary" /> G.O.A.L
          </h2>
          <p className="text-sm text-muted-foreground">
            Every G.O.A.L record logged for this guest. Shared live with Brand Catalog → Customers → G.O.A.L.
          </p>
        </div>
      ) : (
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="goal-title">
            <Target className="w-5 h-5 text-primary" /> G.O.A.L
          </h2>
          <p className="text-sm text-muted-foreground">Guest Opportunity Action Learning</p>
          <p className="text-sm text-muted-foreground max-w-4xl mt-1">
            Every guest concern is an opportunity to act, learn, and improve. GOAL is not only a complaint
            log. It can also record service recovery, guest suggestions, recurring dissatisfaction, and
            opportunities to improve the overall guest experience.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!scoped && (
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by first name, last name, WhatsApp or email"
              className="pl-9"
              data-testid="goal-search"
            />
          </div>
        )}
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              className={`px-3 py-2 text-sm transition-colors ${status === f.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid={`goal-filter-${f.key}`}
            >
              {f.label} <span className="opacity-70">({counts[f.key] ?? 0})</span>
            </button>
          ))}
        </div>
        <Select value={sourceType} onValueChange={setSourceType}>
          <SelectTrigger className="w-[260px]" data-testid="goal-source-type-filter">
            <SelectValue placeholder="All source types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES} data-testid="goal-source-type-filter-all">All source types</SelectItem>
            {sourceTypes.map((s) => (
              <SelectItem key={s} value={s} data-testid={`goal-source-type-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!scoped && (
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="goal-manage-categories-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Categories
          </Button>
        )}
        {canEdit && (
          <Button onClick={() => setAddOpen(true)} data-testid="goal-add-record-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Record
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {!scoped && <TableHead>Guest</TableHead>}
                <TableHead>Treatment concerned</TableHead>
                <TableHead>Source type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Date reported</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() => setOpenId(r.id)}
                  data-testid={`goal-row-${r.id}`}
                >
                  {!scoped && <TableCell className="font-medium">{r.customer_name}</TableCell>}
                  <TableCell>
                    <span className="text-sm">{r.treatment_name}</span>
                    <p className="text-xs text-muted-foreground">{r.treatment_date} · {r.branch_name}</p>
                  </TableCell>
                  <TableCell data-testid={`goal-row-source-type-${r.id}`}>
                    <span className="text-sm">{r.source_type || "—"}</span>
                  </TableCell>
                  <TableCell>{r.category_name}</TableCell>
                  <TableCell>{r.date_reported}</TableCell>
                  <TableCell>{r.source}</TableCell>
                  <TableCell>{r.assigned_to_name || "—"}</TableCell>
                  <TableCell>
                    <Badge
                      variant={r.status === "resolved" ? "secondary" : "destructive"}
                      className="text-[10px]"
                      data-testid={`goal-row-status-${r.id}`}
                    >
                      {r.status === "resolved" ? "Resolved" : "Unresolved"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {loading && (
                <TableRow><TableCell colSpan={colSpan} className="text-center text-sm text-muted-foreground py-8">Loading…</TableCell></TableRow>
              )}
              {empty && (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center text-sm text-muted-foreground py-10" data-testid="goal-empty">
                    No {status} records{q ? " for this search" : ""} yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <GoalRecordDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        categories={categories}
        sources={sources}
        sourceTypes={sourceTypes}
        managers={managers}
        fixedGuest={scoped ? {
          id: customerId,
          full_name: customerName || customer?.full_name || "",
          whatsapp: customer?.whatsapp || "",
          email: customer?.email || "",
        } : null}
        onSaved={() => { setStatus("unresolved"); loadRecords(); }}
      />
      {openId && (
        <GoalRecordDetail
          recordId={openId}
          open={!!openId}
          onOpenChange={(v) => !v && setOpenId(null)}
          categories={categories}
          sources={sources}
          sourceTypes={sourceTypes}
          managers={managers}
          canEdit={canEdit}
          onChanged={loadRecords}
        />
      )}
      {!scoped && (
        <GoalCategoriesDialog
          open={catOpen}
          onOpenChange={setCatOpen}
          categories={categories}
          canEdit={canEdit}
          onChanged={loadLookups}
        />
      )}
    </div>
  );
}
